import { Box, Button, InputAdornment, Paper, TextField, Typography } from "@leap4/frontend";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { urlService } from "../../routing/urlsService";

interface IProps {
    campaign: string;
    /** The campaign's payout, or a session what-if. Null means nothing values a conversion. */
    payout: number | null;
    /** Overall passes this to accept a session what-if inline; the other tabs do not wire one. */
    onUseForSession?: (value: string) => void;
    children: React.ReactNode;
}

/**
 * The payout is the average the customer states — percentage-based, varying by product, and not
 * recoverable from our own events. Without it Attributed and Predicted cannot value a conversion,
 * so the page asks for one rather than showing figures that are silently missing revenue.
 */
const PayoutGate = ({ campaign, payout, onUseForSession, children }: IProps): JSX.Element => {
    const history = useHistory();
    const [draft, setDraft] = useState("");
    const [dismissed, setDismissed] = useState(false);

    if (payout !== null || dismissed) return <>{children}</>;

    const value = Number(draft);
    const valid = draft !== "" && Number.isFinite(value) && value > 0;

    return (
        <Box sx={{ display: "flex", justifyContent: "center", px: 2, py: 6 }}>
            <Paper variant="outlined" sx={{ p: 3, maxWidth: 560 }}>
                <Typography variant="h6">Set a payout for {campaign}</Typography>
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", lineHeight: 1.6 }}>
                    US dollars earned per conversion. This is the average the customer gives us — the
                    real rate is percentage-based and varies by product, so it cannot be worked out
                    from our own data. Until one is set, <strong>Attributed</strong> and{" "}
                    <strong>Predicted</strong> cannot value a conversion, and every figure built on
                    revenue is blank.
                </Typography>

                {onUseForSession && (
                    <Box sx={{ display: "flex", gap: 1, mt: 2.5, alignItems: "flex-start" }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Payout"
                            autoFocus
                            value={draft}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setDraft(e.target.value)
                            }
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === "Enter" && valid) onUseForSession(draft);
                            }}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            inputProps={{ step: "0.01", min: 0 }}
                            sx={{ width: 160 }}
                        />
                        <Button
                            variant="contained"
                            disabled={!valid}
                            onClick={() => onUseForSession(draft)}
                            sx={{ mt: 0.25 }}
                        >
                            Use for this session
                        </Button>
                    </Box>
                )}

                <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.disabled" }}>
                    {onUseForSession
                        ? "A session value is yours alone and is not saved. Set it on the campaign to make it stick."
                        : "Set it on the campaign to use this tab."}
                </Typography>

                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Button
                        variant={onUseForSession ? "outlined" : "contained"}
                        onClick={() => history.push(urlService.campaignEdit(campaign))}
                    >
                        Set it on the campaign
                    </Button>
                    <Button variant="text" onClick={() => setDismissed(true)}>
                        Continue without it
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default PayoutGate;
